import React, { Suspense } from 'react'
import { useDispatch } from 'react-redux'
import ErrorBoundary from '@commonComponents/ErrorBoundary'
import { Tab, TabItem } from '@commonComponents/formComponents/Tab'
import { CustomLoader } from '@commonComponents/formComponents/loader'
import { ErrorMessage } from '@commonComponents/formComponents/ErrorMessage'
import TranslateContextProvider from '../../NL_App/formSections/TranslateContext'
import RubberBindingInfoProvider from '../../context/rubberBindingInfoProvider'
import { useGetDropdownData } from '@hooks/useGetDropdownData'
import { useGetFormData } from '@hooks/useGetFromData'
import BillingForm from '../../commonComponents/commonBillingTab/index'
import NotesTab from '@EUForms/notes/index.js'

const HeaderContainer = React.lazy(() => import('../../DMS_App/formSections/header'))
const Transport = React.lazy(() => import('../../DMS_App/formSections/transport'))
const ValueBuildUpTab = React.lazy(() => import('../../DMS_App/formSections/valueBuildUpTab'))
const InvoiceTab = React.lazy(() => import('./items'))
const ContainerTab = React.lazy(() => import('../../DMS_App/formSections/containers'))
const DisplayError = React.lazy(() => import('@commonComponents/displayError'))

import ErrorInitializer from '../containers/errorInitializer'
import { Portal } from '@commonComponents/portal.js'
import { MemoizeExchangeRates } from '@commonComponents/ExchangeRates/index.js'
import DisplayErrorServerDMS from './displayErrorServerDMS.js'
import { resetServerError } from '../reducer/serverErrorSlice.js'
import { transportTabActions } from '../reducer/transportSlice.js'
import { useCurrentShipmentType } from '@hooks/useCurrentShipmentType.js'
import DMS_RFI from './DMS_RFI'
import { MemoizeMassEmergencyDocument } from '@commonComponents/massEmergencyDocument'

const DMSForm = ({
  shipmentMode,
  updateShipmentMode,
  updateShipmentId,
  translate,
  rubberBindingInfo,
  kxUser,
  kxUserBasicInfo,
  ...props
}) => {
  const {
    shipmentId,
    countryCode, 
    serviceType,
    version='DMS', 
    isImportShipment, 
    currentJobState,
    currentJobData,
    jobWheels,
    kxEngineUserInfo
  } = useCurrentShipmentType()
  
  const dispatch = useDispatch()
  
  const response = useGetDropdownData({
    "countryCode": countryCode, 
    "subSystem": version, 
    "shipmentType": isImportShipment ? "IMPORT" : "EXPORT" 
  })

  const { loader, isMounted, error } = useGetFormData({
    "countryCode": countryCode,
    "version": version,
    "shipmentType": isImportShipment ? "IMPORT" : "EXPORT",
    "shipmentId":shipmentId
  }, (response) => {
    dispatch(resetServerError())
    const defaultCustomOffices = isImportShipment?['DEC']:['DEC', 'EXT']
    const customOfficesList = response?.data?.eventMessage?.transportTab?.customOfficesList || []
    defaultCustomOffices.forEach((customOfficeType, index) => {
      if (!customOfficesList.some(c => c.type === customOfficeType)) {
        const data = {
          id: Date.now() + index,
          type: customOfficeType
        }
        dispatch(transportTabActions.addItemTransport({
          data: data,
          meta: { tableName: 'customOfficesList' }
        }))
      }
    })
  })

  let content
  if (error) content = <ErrorMessage>An Error Occured In Loading NL Form</ErrorMessage>
  else if (loader) content = <CustomLoader>Preparing Form</CustomLoader>
  else if (isMounted) {
    content = (
      <ErrorBoundary>
        <div style={{ marginTop: '5px' }} className='tabContainer C88'>
          {currentJobState?.includes('RFI') && (
            <DMS_RFI
              shipId={props?.shipId}
              shipmentMode={shipmentMode}
              updateShipmentId={updateShipmentId}
              updateShipmentMode={updateShipmentMode}
            />
          )}
          <TranslateContextProvider translate={translate}>
            <RubberBindingInfoProvider rubberBindingInfo={rubberBindingInfo}>
              <Portal targetElementId="exchangeRatesContainer">
                <MemoizeExchangeRates translate={translate} DropDownReducer='dropDownReducer' />
              </Portal>
              <Portal targetElementId="emergencyDocumentContainer">
                <MemoizeMassEmergencyDocument 
                  brokerGuid={props?.currentJobData?.brokerGuid} 
                  translate={translate} 
                  customStateMapper={props?.customStateMapper}
                />
              </Portal>
              <Suspense fallback={<div>Loading...</div>}>
                <DisplayErrorServerDMS />
                <ErrorInitializer>
                  {({ procedure, isProcI2AorB, repeatableValidationMethods }) => {
                    return (<Tab className='euForm' translate={translate}>
                      <TabItem id='headerTab' title='Header'>
                        <DisplayError tabName="headerTab" />
                        <HeaderContainer
                          procedure={procedure}
                          isProcI2AorB={isProcI2AorB}
                          jobWheels={jobWheels}
                          kxUser={kxUser}
                          shipmentId={shipmentId}
                          version={version}
                          serviceType={serviceType}
                          repeatableValidationMethods={repeatableValidationMethods}
                          lowerEnv={props?.headerProps?.lowerEnv}
                          shipId={props?.shipId}
                        />
                      </TabItem>
                      <TabItem id='transportTab' title='Transport'>
                        <Transport />
                      </TabItem>
                      <TabItem id='containersTab' title='Containers'>
                        <ContainerTab />
                      </TabItem>
                      {!isProcI2AorB && <TabItem id='valueBuildUpTab' title='Value Build Up'>
                        <ValueBuildUpTab />
                      </TabItem>}
                      <TabItem id='itemsTab' title='Items'>
                        <InvoiceTab />
                      </TabItem>
                      {(kxEngineUserInfo?.isEngineUser != 'true') && (
                        <TabItem id='billingTab' title='Billing'>
                          <BillingForm
                            kxUserBasicInfo={kxUserBasicInfo}
                            jobWheels={jobWheels}
                            currentJobData={currentJobData}
                          />
                        </TabItem>
                      )}
                      <TabItem id="notesTab" title='Notes'>
                        <NotesTab
                          shipmentId={shipmentId}
                          fullName={kxUserBasicInfo?.fullName}
                          translate={translate}
                        />
                      </TabItem>
                    </Tab>
                    )
                  }}
                </ErrorInitializer>
              </Suspense>
            </RubberBindingInfoProvider>
          </TranslateContextProvider>
        </div>
        <div className='toast'></div>
      </ErrorBoundary>
    )
  } else {
    return null;
  }
  return content
}
export default React.memo(DMSForm)
